import { logException, trimValues } from 'shared/lib/utils';
import { purchase } from '../api';

/**
 * Get a form for adding to a collection in a collectibles policy.
 *
 * @param {Object} opts The options.
 * @param {String} opts.authToken The Sure API auth token.
 * @param {String} opts.policyId The policy ID.
 * @param {String} opts.categoryCode The country code.
 * @param {String} opts.streetAddress The street address.
 * @param {String} [opts.unit] The line 2 address (unit, apt, etc).
 * @param {String} opts.city The city.
 * @param {String} opts.region The state or region.
 * @param {String} opts.postal The postal code.
 * @param {String} opts.country The country code.
 */
export function getCollectiblesEndorsementAddForm(opts) {
  const payload = trimValues({
    category_code: opts.categoryCode,
    rating_address: {
      line1: opts.streetAddress,
      line2: opts.unit,
      city: opts.city,
      state_or_region: opts.region,
      postal: opts.postal,
      country_code: opts.country,
    },
  });

  return fetch(
    `${process.env.REACT_APP_SURE_API_BASE_URL}/api/user/v2/policies/policy/${
      opts.policyId
    }/collections/add/form`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${opts.authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  ).then(response => {
    if (response.status >= 400) {
      return response.json().then(errorResp => {
        const error = new Error(response.statusText);
        logException(error, {
          policyId: opts.policyId,
          response: errorResp,
        });
        throw error;
      });
    } else {
      return response.json();
    }
  });
}

/**
 * Get a checkout for adding to a collection in a collectibles policy.
 *
 * @param {Object} opts The options.
 * @param {String} opts.authToken The Sure API auth token.
 * @param {String} opts.policyId The policy ID.
 * @param {String} opts.categoryCode The country code.
 * @param {String} opts.streetAddress The street address.
 * @param {String} [opts.unit] The line 2 address (unit, apt, etc).
 * @param {String} opts.city The city.
 * @param {String} opts.region The state or region.
 * @param {String} opts.postal The postal code.
 * @param {String} opts.country The country code.
 * @param {Object} opts.details The survey responses.
 */
export function getCollectiblesEndorsementAddCheckout(opts) {
  const payload = trimValues({
    category_code: opts.categoryCode,
    rating_address: {
      line1: opts.streetAddress,
      line2: opts.unit,
      city: opts.city,
      state_or_region: opts.region,
      postal: opts.postal,
      country_code: opts.country,
    },
    details: opts.details,
  });

  return fetch(
    `${process.env.REACT_APP_SURE_API_BASE_URL}/api/user/v2/policies/policy/${
      opts.policyId
    }/collections/add/checkout`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${opts.authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  ).then(response => {
    if (response.status >= 400) {
      return response.json().then(errorResp => {
        const error = new Error(response.statusText);
        logException(error, {
          policyId: opts.policyId,
          response: errorResp,
        });
        throw error;
      });
    } else {
      return response.json();
    }
  });
}

/**
 * Get a form for editing a collection in a collectibles policy.
 *
 * @param {Object} opts The options.
 * @param {String} opts.authToken The Sure API auth token.
 * @param {String} opts.policyId The policy ID.
 * @param {String} opts.categoryCode The country code.
 * @param {String} opts.streetAddress The street address.
 * @param {String} [opts.unit] The line 2 address (unit, apt, etc).
 * @param {String} opts.city The city.
 * @param {String} opts.region The state or region.
 * @param {String} opts.postal The postal code.
 * @param {String} opts.country The country code.
 */
export function getCollectiblesEndorsementEditForm(opts) {
  const payload = trimValues({
    category_code: opts.categoryCode,
    rating_address: {
      line1: opts.streetAddress,
      line2: opts.unit,
      city: opts.city,
      state_or_region: opts.region,
      postal: opts.postal,
      country_code: opts.country,
    },
  });

  return fetch(
    `${process.env.REACT_APP_SURE_API_BASE_URL}/api/user/v2/policies/policy/${
      opts.policyId
    }/collections/edit/form`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${opts.authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  ).then(response => {
    if (response.status >= 400) {
      return response.json().then(errorResp => {
        const error = new Error(response.statusText);
        logException(error, {
          policyId: opts.policyId,
          response: errorResp,
        });
        throw error;
      });
    } else {
      return response.json();
    }
  });
}

/**
 * Get a checkout for editing a collection in a collectibles policy.
 *
 * @param {Object} opts The options.
 * @param {String} opts.authToken The Sure API auth token.
 * @param {String} opts.policyId The policy ID.
 * @param {String} opts.categoryCode The country code.
 * @param {String} opts.streetAddress The street address.
 * @param {String} [opts.unit] The line 2 address (unit, apt, etc).
 * @param {String} opts.city The city.
 * @param {String} opts.region The state or region.
 * @param {String} opts.postal The postal code.
 * @param {String} opts.country The country code.
 * @param {Object} opts.details The survey responses.
 */
export function getCollectiblesEndorsementEditCheckout(opts) {
  const payload = trimValues({
    category_code: opts.categoryCode,
    rating_address: {
      line1: opts.streetAddress,
      line2: opts.unit,
      city: opts.city,
      state_or_region: opts.region,
      postal: opts.postal,
      country_code: opts.country,
    },
    details: opts.details,
  });

  return fetch(
    `${process.env.REACT_APP_SURE_API_BASE_URL}/api/user/v2/policies/policy/${
      opts.policyId
    }/collections/edit/checkout`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${opts.authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  ).then(response => {
    if (response.status >= 400) {
      return response.json().then(errorResp => {
        const error = new Error(response.statusText);
        logException(error, {
          policyId: opts.policyId,
          response: errorResp,
        });
        throw error;
      });
    } else {
      return response.json();
    }
  });
}

/**
 * Get a checkout for removing a collection from a collectibles policy.
 *
 * @param {Object} opts The options.
 * @param {String} opts.authToken The Sure API auth token.
 * @param {String} opts.policyId The policy ID.
 * @param {String} opts.categoryCode The country code.
 */
export function getCollectiblesEndorsementRemoveCheckout(opts) {
  return fetch(
    `${process.env.REACT_APP_SURE_API_BASE_URL}/api/user/v2/policies/policy/${
      opts.policyId
    }/collections/remove/checkout`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${opts.authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        category_code: opts.categoryCode,
      }),
    }
  ).then(response => {
    if (response.status >= 400) {
      return response.json().then(errorResp => {
        const error = new Error(response.statusText);
        logException(error, {
          policyId: opts.policyId,
          response: errorResp,
        });
        throw error;
      });
    } else {
      return response.json();
    }
  });
}

/**
 * Submit a removal of a collection for a collectibles policy.
 *
 * @param {Object} opts The options.
 * @param {String} opts.authToken The Sure API auth token.
 * @param {String} opts.policyId The policy ID.
 * @param {String} opts.categoryCode The country code.
 */
export function submitCollectiblesEndorsementRemove(opts) {
  return fetch(
    `${process.env.REACT_APP_SURE_API_BASE_URL}/api/user/v2/policies/policy/${
      opts.policyId
    }/collections/remove/submit`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${opts.authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        category_code: opts.categoryCode,
      }),
    }
  );
}

/**
 * Submit an (add) collectibles endorsement.
 *
 * @param {Object} opts The options.
 * @param {String} [opts.authToken] The Sure API auth token.
 * @param {String} opts.categoryCode The collectibles category code.
 * @param {String} opts.policyId The policy ID.
 * @param {Object} opts.details The survey responses.
 * @param {String} opts.streetAddress The user's street address.
 * @param {String} opts.unit The user's unit.
 * @param {String} opts.city The user's city.
 * @param {String} opts.region The user's state or region.
 * @param {String} opts.postal The user's postal code.
 * @param {Number} opts.lat The user's lat location coordinate.
 * @param {Number} opts.lng The user's lng location coordinate.
 * @param {String} opts.firstName The user's first name.
 * @param {String} opts.lastName The user's last name.
 * @param {String} opts.email The user's email address.
 * @param {String} opts.phoneNumber The user's email phone number.
 * @param {Object} opts.cardElement The Stripe card element.
 * @param {Number} opts.revenue The total revenue of the purchase.
 * @param {Object} [opts.metadata] Any metadata properties to attach.
 */
export function purchaseCollectiblesEndorsementAdd(opts) {
  const payload = {
    category_code: opts.categoryCode,
    details: opts.details,
    rating_address: {
      line1: opts.streetAddress,
      line2: opts.unit,
      city: opts.city,
      state_or_region: opts.region,
      postal: opts.postal,
      country_code: opts.country,
    },
    metadata: opts.metadata,
  };

  return purchase({
    planId: opts.planId,
    firstName: opts.firstName,
    lastName: opts.lastName,
    email: opts.email,
    phoneNumber: opts.phoneNumber,
    authToken: opts.authToken,
    cardElement: opts.cardElement,
    revenue: opts.revenue,
    payload,
    prefix: 'Collectibles Endorsement',
    endpoint: `/api/user/v2/policies/policy/${
      opts.policyId
    }/collections/add/submit`,
  });
}

/**
 * Submit an (edit) collectibles endorsement.
 *
 * @param {Object} opts The options.
 * @param {String} [opts.authToken] The Sure API auth token.
 * @param {String} opts.categoryCode The collectibles category code.
 * @param {String} opts.policyId The policy ID.
 * @param {Object} opts.details The survey responses.
 * @param {String} opts.streetAddress The user's street address.
 * @param {String} opts.unit The user's unit.
 * @param {String} opts.city The user's city.
 * @param {String} opts.region The user's state or region.
 * @param {String} opts.postal The user's postal code.
 * @param {Number} opts.lat The user's lat location coordinate.
 * @param {Number} opts.lng The user's lng location coordinate.
 * @param {String} opts.firstName The user's first name.
 * @param {String} opts.lastName The user's last name.
 * @param {String} opts.email The user's email address.
 * @param {String} opts.phoneNumber The user's email phone number.
 * @param {Object} opts.cardElement The Stripe card element.
 * @param {Number} opts.revenue The total revenue of the purchase.
 * @param {Object} [opts.metadata] Any metadata properties to attach.
 */
export function purchaseCollectiblesEndorsementEdit(opts) {
  const payload = {
    category_code: opts.categoryCode,
    details: opts.details,
    rating_address: {
      line1: opts.streetAddress,
      line2: opts.unit,
      city: opts.city,
      state_or_region: opts.region,
      postal: opts.postal,
      country_code: opts.country,
    },
    metadata: opts.metadata,
  };

  return purchase({
    planId: opts.planId,
    firstName: opts.firstName,
    lastName: opts.lastName,
    email: opts.email,
    phoneNumber: opts.phoneNumber,
    authToken: opts.authToken,
    cardElement: opts.cardElement,
    revenue: opts.revenue,
    payload,
    prefix: 'Collectibles Endorsement',
    endpoint: `/api/user/v2/policies/policy/${
      opts.policyId
    }/collections/edit/submit`,
  });
}
